/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable unused-imports/no-unused-vars */
import {
  useGetLiveFormQuery,
  useSaveLiveFormMutation,
  useUpdateLiveFormMutation,
} from '@gen2/api/live-form/hooks';
import { LiveFormType } from '@gen2/app/invites/send-invites/store';
import { useAuth, useToast } from '@gen2/hooks';
import { TUser } from '@gen2/types/user';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Dialog } from '@mui/material';
import NutrientViewer, {
  AnnotationsUnion,
  Instance,
} from '@nutrient-sdk/viewer';
import { customDecode } from '@nx-fe/components';
import { AxiosError } from 'axios';
import { isEmpty } from 'lodash';
import randomstring from 'randomstring';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useUnmount } from 'usehooks-ts';
import { onDragStart } from './nutrient/sidePanel/helpers';
import SidePanel from './nutrient/sidePanel/sidePanel';
import {
  createDebouncedAnnotationHandlers,
  CustomInstantJSON,
  extractAnnotationsContacts,
  generateInstantJSON,
  handleAnnotationCreation,
  handleAnnotationDelete,
  TOOLBAR_ITEMS,
} from './nutrient/utils/common-helpers';
import getAnnotationRenderers from './nutrient/utils/customAnnotationRenderer';
import customizeUIForInitials from './nutrient/utils/customizeUIForInitials';
import handleDrop from './nutrient/utils/dropAnnotations';
import { TAssignee } from './nutrient/utils/types';
import {
  HeaderTitle,
  StyledHeader,
  StyledHeaderActions,
  StyledViewer,
  StyledWebViewerBox,
} from './peview-web-viewer-modal.styled';
export interface FormViewerModalProps {
  fileId: string;
  fileName: string;
  onClose: () => void;
  open: boolean;
  fileUrl: string;
  type: LiveFormType;
}

const defaultAssignee = {
  id: '1',
  first_name: 'Contact',
  last_name: '1',
  email: 'Contact 1',
};

let renderCount = 0;
export const TemplateNutrientWebViewerModal = ({
  fileName,
  onClose,
  open,
  fileId,
  type,
  fileUrl,
}: FormViewerModalProps) => {
  const toast = useToast();
  const viewer = useRef(null);
  const { user, featureFlags } = useAuth();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const liveFormType: LiveFormType = type;

  const [contacts, setContacts] = useState<TAssignee[]>([defaultAssignee]);

  const { mutate: saveMutate, isLoading: saveLoading } =
    useSaveLiveFormMutation();
  const { mutate: updateMutate, isLoading: updateLoading } =
    useUpdateLiveFormMutation();

  // Solution：solely to work around an issue that only appears in Development mode,
  // forcing WebViewer Initialization Only Occurs Once in Development build -- the Strict Mode
  const beenInitialised = useRef<boolean>(false);

  const [nutrientInstance, setNutrientInstance] = useState<any>(null);

  const initialAssignee = defaultAssignee;

  const [currSignee, setCurrSignee] = useState(initialAssignee);
  const currSigneeRef = useRef(currSignee);
  currSigneeRef.current = currSignee;

  const [currUser] = useState<TAssignee>(user as TAssignee);
  const currUserRef = useRef(currUser);
  currUserRef.current = currUser;

  // State to store the current page index
  const [onPageIndex, setOnPageIndex] = useState<number>(0);
  const onPageIndexRef = useRef(onPageIndex);
  onPageIndexRef.current = onPageIndex;
  
  const [isSignatureTypeEnabled, setIsSignatureTypeEnabled] = useState(true);
  
  const { data } = useGetLiveFormQuery(fileId);

  const instantJSON: CustomInstantJSON = useMemo(() => {
    const initialJsonData: CustomInstantJSON = {
      format: 'https://pspdfkit.com/instant-json/v1',
      customFromAPI: true,
    };

    if (data !== undefined) {
      const responseData = data?.liveform?.fields?.json_data;

      if (responseData) {
        return {
          ...initialJsonData,
          ...responseData,
        };
      }

      return initialJsonData;
    }

    return {
      ...initialJsonData,
      customFromAPI: false,
    };
  }, [data]);

  const liveFormFieldId = useMemo(() => {
    if (data?.liveform?.fields?.id) {
      return data?.liveform?.fields?.id;
    }

    return '';
  }, [data?.liveform?.fields?.id]);

    useEffect(() => {
      if (data?.liveform?.fields?.is_type_signature_enabled !== undefined) {
        setIsSignatureTypeEnabled(data.liveform.fields.is_type_signature_enabled);
      }
    }, [data?.liveform?.fields?.is_type_signature_enabled]);

  // For Custom signature / initial field appearance
  const mySignatureIdsRef = useRef([]);
  const [signatureAnnotationIds, setSignatureAnnotationIds] = useState<
    string[]
  >([]);

  // For Custom Add Signature / Intitial field appearance
  const [sessionSignatures, setSessionSignatures] = useState<any>([]);
  const [sessionInitials, setSessionInitials] = useState<any>([]);

  // Hack: Store the last duplicated radio button bounding box for manual injection
  const [lastDuplicatedRadioButtonBbox, setLastDuplicatedRadioButtonBbox] =
    useState<{
      annotationId: string;
      bbox: number[];
    } | null>(null);

  function onDragEnd(event: React.DragEvent<HTMLDivElement>) {
    (event.target as HTMLDivElement).style.opacity = '1';
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const [isTextAnnotationMovable, setIsTextAnnotationMovable] = useState(false);
  const isTextAnnotationMovableRef = useRef(isTextAnnotationMovable);
  isTextAnnotationMovableRef.current = isTextAnnotationMovable;

  // Tracking whether add Signature/Initial UI
  let isCreateInitial = false;
  const trackInst = useRef<any>(null);
  const loadPSPDFKit = useCallback(
    async (instantJSON: CustomInstantJSON, el?: HTMLElement) => {
      if (NutrientViewer && fileUrl && el && !beenInitialised.current) {
        NutrientViewer.unload(el);
        beenInitialised.current = true;
        try {
          const {
            UI: { createBlock, Recipes, Interfaces },
          } = NutrientViewer;

          const instance = await NutrientViewer.load({
            theme: NutrientViewer.Theme.LIGHT,
            container: el,
            document: fileUrl,
            instantJSON: instantJSON,
            licenseKey: customDecode(window.$xr9c42),
            baseUrl: `${window.location.protocol}//${window.location.host}/assets/lib/pspdfkit/`,
            toolbarItems: [...TOOLBAR_ITEMS].filter(
              (item) => !['export-pdf', 'print'].includes(item.type),
            ),
            disableTextSelection: false,
            autoSaveMode: NutrientViewer.AutoSaveMode.IMMEDIATE,
            initialViewState: new NutrientViewer.ViewState({
              interactionMode: NutrientViewer.InteractionMode.FORM_CREATOR,
            }),
            styleSheets: [`/assets/lib/pspdfkit/viewer.css`],
            isEditableAnnotation: (annotation) => !annotation.isSignature,
            ui: {
              [Interfaces.CreateSignature]: ({ props }: { props: any }) => ({
                content: createBlock(
                  Recipes.CreateSignature,
                  props,
                  ({ ui }: { ui: any }) => {
                    if (isCreateInitial) ui = customizeUIForInitials(ui);
                    return ui.createComponent();
                  },
                ).createComponent(),
              }),
            },
            customRenderers: {
              Annotation: ({
                annotation,
              }: {
                annotation: AnnotationsUnion;
              }) => {
                return getAnnotationRenderers({
                  annotation,
                  instance: trackInst.current,
                });
              },
            },
          });

          setLoading(false);
          setNutrientInstance(instance);
        } catch (error) {
          console.error('Error loading PSPDFKit:', error);
        }
      } else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        setTimeout(() => loadPSPDFKit(instantJSON, viewer.current!));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const configureInstanceEvents = useCallback(
    (inst: Instance) => {
      inst.setViewState((viewState) =>
        viewState.set(
          'showSignatureValidationStatus',
          NutrientViewer.ShowSignatureValidationStatusMode.IF_SIGNED,
        ),
      );

      inst.addEventListener('viewState.currentPageIndex.change', (page) =>
        setOnPageIndex(page),
      );

      //@ts-ignore
      const cont = inst.contentDocument.host;
      cont.ondrop = async (e: any) =>
        await handleDrop(
          e,
          inst,
          NutrientViewer,
          currSigneeRef,
          currUserRef,
          onPageIndexRef,
        );

      const { handleDuplicateButton, handleRemoveDateTimeField, cleanup } =
        createDebouncedAnnotationHandlers(
          inst,
          featureFlags,
          storeDuplicatedRadioButtonBbox,
        );

      debouncedHandlersCleanupRef.current = cleanup;

      inst.addEventListener(
        'annotations.press',
        (event: { preventDefault?: any; annotation?: any }) => {
          if (!viewer.current) {
            return;
          }
          const { annotation } = event;
          if (annotation.customData?.isInitial) {
            setSessionInitials((prev: any) => [...prev, annotation]);
            isCreateInitial = true;
          } else {
            setSessionSignatures((prev: any) => [...prev, annotation]);
            isCreateInitial = false;
          }

          if (
            !isTextAnnotationMovableRef.current &&
            annotation instanceof NutrientViewer.Annotations.TextAnnotation
          ) {
            event.preventDefault();
          }

          handleDuplicateButton(annotation);
          handleRemoveDateTimeField(annotation);

          if (
            !isTextAnnotationMovableRef.current &&
            event.annotation instanceof
              NutrientViewer.Annotations.TextAnnotation
          ) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            event.preventDefault();
          }
        },
      );

      inst.addEventListener('annotations.transform', (event) => {
        const { annotation }: { annotation?: any } = event;
        handleDuplicateButton(annotation);
      });

      inst.addEventListener(
        'storedSignatures.create',
        async (annotation: any) => {
          if (isCreateInitial) {
            setSessionInitials([...sessionInitials, annotation]);
          } else {
            setSessionSignatures([...sessionSignatures, annotation]);
          }
        },
      );

      inst.addEventListener(
        'annotations.create',
        async function (createdAnnotations: any) {
          const annotation = createdAnnotations.get(0);
          await handleAnnotationCreation(
            inst,
            annotation,
            mySignatureIdsRef,
            setSignatureAnnotationIds,
            currUser.email,
          );
        },
      );

      inst.addEventListener('annotations.update', async (event) => {
        const annotation = event.get(0) as AnnotationsUnion;

        // Update stored bounding box if this is the tracked radio button
        if (annotation?.id === lastDuplicatedRadioButtonBbox?.annotationId) {
          const updatedBbox = [
            annotation.boundingBox.left,
            annotation.boundingBox.top,
            annotation.boundingBox.width,
            annotation.boundingBox.height,
          ];
          setLastDuplicatedRadioButtonBbox({
            annotationId: annotation.id,
            bbox: updatedBbox,
          });
        }
      });

      inst.addEventListener(
        'annotations.delete',
        async (deletedAnnotations: any) => {
          const annotation = deletedAnnotations.get(0);
          await handleAnnotationDelete(inst, annotation);
          const updatedAnnotationIds = mySignatureIdsRef.current.filter(
            (id) => id !== annotation.id,
          );
          setSignatureAnnotationIds(updatedAnnotationIds);
          mySignatureIdsRef.current = updatedAnnotationIds;
        },
      );

      inst.setViewState((viewState) =>
        viewState.set(
          'interactionMode',
          NutrientViewer.InteractionMode.FORM_CREATOR,
        ),
      );
      setIsTextAnnotationMovable(true);
    },
    [featureFlags, lastDuplicatedRadioButtonBbox],
  );

  useEffect(() => {
    if (nutrientInstance) {
      trackInst.current = nutrientInstance;
      configureInstanceEvents(nutrientInstance);
    }
  }, [configureInstanceEvents, nutrientInstance]);

  const cleanupPSPDFKit = async () => {
    if (nutrientInstance && viewer.current) {
      await NutrientViewer.unload(viewer.current);
      viewer.current = null;
      setNutrientInstance(null);
      setLastDuplicatedRadioButtonBbox(null);
    }
  };

  const debouncedHandlersCleanupRef = useRef<(() => void) | null>(null);

  // Watcher of everything inside the viewer container including the shadow DOM
  // NOTE: This is a temporary fix until we get a permanent solution from NUTRIENT
  useEffect(() => {
    if (!open || !viewer.current) return;

    const observer = new MutationObserver((mutations) => {
      const el = viewer.current;
      if (!el) return;

      // Check for shadow DOM on first child
      const shadowRoot = (el as HTMLElement).firstElementChild?.shadowRoot;

      if (shadowRoot) {
        // Set up a separate observer for the shadow DOM content
        const shadowObserver = new MutationObserver((shadowMutations) => {
          const underlay: HTMLElement | null = shadowRoot.querySelector(
            '[data-testid="underlay"]',
          );

          if (underlay) {
            (underlay.parentNode as HTMLElement).classList.add('fixed-to-top');

            shadowObserver.disconnect();
            observer.disconnect();
          }
        });

        // Observe the shadow DOM content directly
        shadowObserver.observe(shadowRoot, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }
    });

    // Start observing the viewer container with more comprehensive options
    observer.observe(viewer.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeOldValue: true,
    });

    // Cleanup observer on unmount
    return () => {
      observer.disconnect();
    };
  }, [open, viewer.current]); // Added viewer.current as dependency

  useEffect(() => {
    if (
      instantJSON &&
      instantJSON.customFromAPI &&
      viewer.current &&
      !isEmpty(instantJSON) &&
      renderCount === 0
    ) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      loadPSPDFKit(instantJSON, viewer.current!);
      const extractedContacts = extractAnnotationsContacts(instantJSON);
      if (extractedContacts && extractedContacts.length > 0) {
        setContacts(extractedContacts);
      } else {
        setContacts([defaultAssignee]);
      }
      renderCount++;
    }
  }, [instantJSON, loadPSPDFKit]);

  useUnmount(async () => {
    if (debouncedHandlersCleanupRef.current) {
      debouncedHandlersCleanupRef.current();
    }
    await cleanupPSPDFKit();
    renderCount = 0;
  });

  // Hack: Function to store duplicated radio button bounding box
  const storeDuplicatedRadioButtonBbox = (
    annotationId: string,
    bbox: number[],
  ) => {
    setLastDuplicatedRadioButtonBbox({ annotationId, bbox });
  };

  const onSave = async () => {
    await nutrientInstance.save();

    const instantJSONPayload = await nutrientInstance.exportInstantJSON();

    // Hack: Manually inject the correct bounding box for the last duplicated radio button
    if (lastDuplicatedRadioButtonBbox && instantJSONPayload.annotations) {
      const annotationIndex = instantJSONPayload.annotations.findIndex(
        (annotation: any) =>
          annotation.id === lastDuplicatedRadioButtonBbox.annotationId,
      );

      if (annotationIndex !== -1) {
        instantJSONPayload.annotations[annotationIndex].bbox =
          lastDuplicatedRadioButtonBbox.bbox as [
            number,
            number,
            number,
            number,
          ];

        // Clear the stored data after injection
        setLastDuplicatedRadioButtonBbox(null);
      }
    }

    const payloadInstantJSON = await generateInstantJSON(instantJSONPayload);

    const responseHandlers = {
      onSuccess: () => {
        toast.show({
          text: 'File data saved successfully',
          variant: 'success',
        });

        handleClose();
      },
      onError: (e: unknown) => {
        if ((e as AxiosError).status === 500) {
          toast.show({
            text: 'Internal Server Error',
            variant: 'error',
          });
        } else if ((e as AxiosError).status === 422) {
          toast.show({
            text: 'The selected type is invalid',
            variant: 'error',
          });
        }
      },
    };

    if (liveFormFieldId) {
      updateMutate(
        {
          fileId,
          liveFormFieldId,
          liveFormData: {
            xfdf_data: '',
              json_data: JSON.stringify(payloadInstantJSON),
              type: liveFormType,
              is_type_signature_enabled: isSignatureTypeEnabled,
          },
        },
        responseHandlers,
      );
    } else {
      saveMutate(
        {
          fileId,
          liveFormData: {
            xfdf_data: '',
              json_data: JSON.stringify(payloadInstantJSON),
              type: liveFormType,
              is_type_signature_enabled: isSignatureTypeEnabled,
          },
        },
        responseHandlers,
      );
    }
  };

  const applyAndSave = async () => {
    try {
      await onSave();
    } catch (e) {
      console.error(e);
      toast.show({
        text: 'Something went wrong! Reload the page and try again',
        variant: 'error',
      });
    }
  };

  const handleClose = async () => {
    await cleanupPSPDFKit();
    onClose();
  };

  const onChangeReadyToSign = async (
    value: boolean,
    user: TUser | null,
    PSPDFKit: any,
  ) => {
    if (nutrientInstance) {
      if (value) {
        nutrientInstance.setViewState((viewState: any) =>
          viewState.set('interactionMode', PSPDFKit.InteractionMode.PAN),
        );
        nutrientInstance.setViewState((viewState: any) =>
          viewState.set('showToolbar', false),
        );
        setIsTextAnnotationMovable(false);
      } else {
        nutrientInstance.setViewState((viewState: any) =>
          viewState.set(
            'interactionMode',
            PSPDFKit.InteractionMode.FORM_CREATOR,
          ),
        );
        nutrientInstance.setViewState((viewState: any) =>
          viewState.set('showToolbar', true),
        );
        setIsTextAnnotationMovable(true);
      }
    }
  };

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    onChangeReadyToSign(event.target.checked, user, NutrientViewer);
  };

  const handleSignatureTypeChange = useCallback(
    (enabled: boolean) => {
      setIsSignatureTypeEnabled(enabled);
      // State is updated, will be saved when the form is submitted
    },
    [],
  );

  const handleAddContact = useCallback(() => {
    if (contacts.length >= 10) {
      toast.show({
        text: 'Maximum 10 contact placeholders allowed.',
        variant: 'info',
      });
      return;
    }

    const usedIndices = new Set<number>();
    contacts.forEach((c) => {
      const match = /^Contact (\d+)$/.exec(c.email);
      if (match) {
        usedIndices.add(Number(match[1]));
      }
    });

    let nextIndex = 1;
    while (usedIndices.has(nextIndex)) {
      nextIndex++;
    }

    const newContact: TAssignee = {
      id: randomstring.generate(7),
      first_name: 'Contact',
      last_name: nextIndex.toString(),
      email: `Contact ${nextIndex}`,
    };

    setContacts((prev) => {
      const updated = [...prev, newContact];
      return updated.sort((a, b) => {
        const aMatch = /^Contact (\d+)$/.exec(a.email);
        const bMatch = /^Contact (\d+)$/.exec(b.email);
        const aIndex = aMatch ? Number(aMatch[1]) : 0;
        const bIndex = bMatch ? Number(bMatch[1]) : 0;
        return aIndex - bIndex;
      });
    });
  }, [contacts, toast]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedContact = contacts.find(
        (contact) => contact.email === event.target.value,
      );
      if (selectedContact) {
        setCurrSignee(selectedContact);
      }
    },
    [contacts],
  );

  return (
    <Dialog
      id="template-nutrient-viewer-modal"
      fullScreen
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
    >
      <StyledHeader>
        <HeaderTitle>{fileName}</HeaderTitle>
        <StyledHeaderActions spacing={1} direction="row">
          <Button variant="outlined" color="tertiary" onClick={handleClose}>
            Back
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={applyAndSave}
            loading={saveLoading || updateLoading}
          >
            Save & close
          </LoadingButton>
        </StyledHeaderActions>
      </StyledHeader>
      <StyledWebViewerBox>
        <SidePanel
          users={contacts}
          currSignee={currSignee}
          onDragStart={(
            event: React.DragEvent<HTMLDivElement>,
            type: string,
            dateFormat?: string,
          ) =>
            onDragStart(
              event,
              type,
              currSignee,
              nutrientInstance,
              liveFormType,
              1,
              dateFormat,
            )
          }
          onDragEnd={onDragEnd}
          isTemplate={true}
          type={liveFormType}
          checked={checked || loading}
          handleCheck={handleCheck}
          handleChange={handleChange}
          onAddContact={handleAddContact}
          isSignatureTypeEnabled={isSignatureTypeEnabled}
          onSignatureTypeChange={handleSignatureTypeChange}
        />
        <StyledViewer ref={viewer} onDragOver={handleDragOver} />
      </StyledWebViewerBox>
    </Dialog>
  );
};
